import { useState } from 'react'

import { placeLabel } from '../portal/presence.js'
import { avatarToken } from '../trips/EntryCard.jsx'

// The drawing half of presence. P6-242b — the data half is `portal/presence.js`
// and the argument for everything below is the approved handoff
// (`docs/design-briefs/portal/HANDOFF-presence.md`) plus the header handoff's §3.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ONE COMPONENT, TWO HOMES, NEVER BOTH AT ONCE — AND CSS DECIDES WHICH
// ═══════════════════════════════════════════════════════════════════════════
//
// The disc group lives in the second row below 1120px and in the top bar from
// 1120 up. Both are rendered; `.d2d-presence--strip` and `.d2d-presence--bar`
// each switch themselves off at the other's side of the breakpoint. Doing it in
// CSS, rather than reading a viewport width in a hook, means there is no frame in
// which the wrong one is painted and no render that depends on a measurement —
// and it is the same breakpoint the stylesheet's other rules already key on, so
// the two cannot disagree about where 1120 is.
//
// The disc COUNT works the same way, for the same reason. The strip renders six
// and the stylesheet hides the fifth and sixth below 768 (four at every width
// below it, 336 and under included: P6-343, the design's `cap = phone ? 4 : 6`),
// with one `+N` per cap and CSS showing the one that matches. A hook that
// asked "how many fit" would be a second implementation of a rule the stylesheet
// already owns.

/** The most discs the strip draws, at its widest. The stylesheet trims it. */
export const STRIP_DISCS = 6
/** The most the bar draws, at any width — the handoff's 128px cap. */
export const BAR_DISCS = 4
/** The strip's other caps, for the `+N` that goes with each. Both are four now (P6-343); they stay two names because the
 * stylesheet shows one `+N` per width tier, and a future width that wants a different count has a name to change. */
export const STRIP_DISCS_PHONE = 4
export const STRIP_DISCS_NARROW = 4

/**
 * A person's colour for a DOT, which is never ember.
 *
 * The handoff says it twice — *"filled with that person's avatar colour"* and
 * *"the dot is never ember. One ember fill per screen, and on the portal that is
 * Skip"* — and `avatarToken` is the site's per-person colour, whose first bucket
 * IS the ember accent. Both cannot hold for that bucket, so the rule that is
 * stated as a prohibition wins: an ember person's dot is the peach tint of the
 * same hue. A disc is still their own colour; only the 6px dot on the nav steps
 * aside, and the names are in the link's title and accessible name either way.
 */
function dotToken(userId) {
  const token = avatarToken(userId)
  return token === '--d2d-accent' ? '--d2d-ember-300' : token
}

/** The fact, in words, for the button's accessible name. */
export function presenceName(people, youId) {
  const only = people.length === 1
  if (only && people[0].userId === youId) return 'Only you have the portal open. Show who.'
  return only
    ? '1 person has the portal open. Show who.'
    : `${people.length} people have the portal open. Show who.`
}

function countLabel(people, youId, open) {
  if (open) return 'Close'
  if (people.length === 1 && people[0].userId === youId) return 'Just you'
  return `${people.length} here`
}

/**
 * One member's disc: their Discord picture, or a colour that is theirs.
 *
 * The fallback is the colour `avatarToken(userId)` already gives this person
 * everywhere else on the site — so a failed image, a blocked request and a member
 * with no picture all land on the same disc, and nothing ever draws a broken
 * image. Never a letter: at 22px a letter is noise.
 */
export function Disc({ person, you = false, rank, row = false }) {
  const [broken, setBroken] = useState(false)
  const src = broken ? null : person.avatarUrl
  return (
    <span
      className={`d2d-pdisc${row ? ' d2d-pdisc--row' : ''}${you ? ' d2d-pdisc--you' : ''}`}
      data-rank={rank}
      style={{ background: `var(${avatarToken(person.userId)})` }}
      aria-hidden="true"
    >
      {src ? (
        <img
          className="d2d-pdisc__img"
          src={src}
          alt=""
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
        />
      ) : null}
    </span>
  )
}

function More({ people, cap, tier }) {
  const rest = people.length - cap
  if (rest <= 0) return null
  return (
    <span className={`d2d-presence__more d2d-presence__more--${tier} d2d-micro`} aria-hidden="true">
      +{rest}
    </span>
  )
}

/**
 * The disc group as a button. `variant` is where it lives: `strip` (the second
 * row, below 1120) or `bar` (the top bar, from 1120).
 *
 * The whole thing is ONE `<button>`, which is how the 44px floor is met without
 * making a 22px disc bigger. `aria-expanded` and an accessible name that states
 * the fact; the discs and the `+N` are decoration for the eye and `aria-hidden`.
 */
export function PresenceButton({ variant, people, youId, open, onToggle }) {
  const strip = variant === 'strip'
  const shown = people.slice(0, strip ? STRIP_DISCS : BAR_DISCS)
  return (
    <button
      type="button"
      className={`d2d-presence d2d-presence--${variant}`}
      aria-expanded={open ? 'true' : 'false'}
      aria-controls={open ? 'd2d-presence-panel' : undefined}
      aria-label={open ? 'Close the list of who has the portal open.' : presenceName(people, youId)}
      data-testid={`presence-${variant}`}
      onClick={(event) => onToggle(event.currentTarget)}
    >
      <span className="d2d-presence__discs">
        {shown.map((person, index) => (
          <Disc
            key={`${person.userId}|${person.avatarUrl ?? ''}`}
            person={person}
            you={person.userId === youId}
            rank={index + 1}
          />
        ))}
        {strip ? (
          <>
            <More people={people} cap={STRIP_DISCS} tier="wide" />
            <More people={people} cap={STRIP_DISCS_PHONE} tier="phone" />
            <More people={people} cap={STRIP_DISCS_NARROW} tier="narrow" />
          </>
        ) : (
          <More people={people} cap={BAR_DISCS} tier="bar" />
        )}
      </span>
      {strip ? (
        <span className="d2d-presence__count d2d-micro">{countLabel(people, youId, open)}</span>
      ) : null}
    </button>
  )
}

/** The expanded list: one 44px row a member, the same fact the nav dots carry,
 * spelled out. It is a list, not a sheet — six rows and then it scrolls. */
export function PresenceList({ people, youId }) {
  return (
    <div className="d2d-topbar__drop" id="d2d-presence-panel" data-testid="presence-panel">
      <div className="d2d-page">
        <div
          className="d2d-drop__scroll d2d-drop__scroll--people"
          role="region"
          aria-label="Who has the portal open"
          tabIndex={0}
        >
          <ul className="d2d-drop__people">
            {people.map((person) => {
              const you = person.userId === youId
              const where = [you ? 'You' : null, placeLabel(person.path)].filter(Boolean).join(' · ')
              return (
                <li key={person.userId} className="d2d-drop__person">
                  <Disc person={person} you={you} row />
                  <span className="d2d-drop__name">{person.displayName}</span>
                  {where === '' ? null : <span className="d2d-drop__where d2d-micro">{where}</span>}
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </div>
  )
}

/**
 * The 6px dot on a nav pill or tab: who else is on that section.
 *
 * Nobody → NO dot (the caller does not render this). One person → filled with
 * theirs. Two or more → the same fill plus a ring: shape, not only hue, carries
 * the difference. It is never ember (one ember fill per screen, and the portal's
 * is Skip) and never the only carrier of the names, which go in the link's
 * `title` and accessible name.
 */
export function PresenceDot({ people }) {
  if (people.length === 0) return null
  return (
    <span
      className={`d2d-presencedot${people.length > 1 ? ' d2d-presencedot--many' : ''}`}
      style={{ background: `var(${dotToken(people[0].userId)})` }}
      data-testid="presence-dot"
      aria-hidden="true"
    />
  )
}
